/**
 * Pure MapDocument → SVG string (#167). Safe by construction: escaped text,
 * no script / foreignObject / external image links. Assets and landmass masks
 * are embedded only when callers pass data URLs.
 */

import { fillPoints, fillsBackToFront, MAP_FILL_ALPHA } from "./mapFill";
import {
  catmullRomCubicControls,
  landmarkDrawSize,
  pathLineDash,
  pathPoints,
  worldToScreen,
  type ScreenPoint,
  type WorldPoint,
} from "./mapGeometry";
import { mapImageDrawSize } from "./mapImageTransform";
import { layoutMapLabel } from "./mapLabelLayout";
import { landmarkIconById } from "./mapLandmarkIcons";
import {
  isLabelsVisible,
  isPaperVisible,
  labelFontPx,
  labelInk,
  landmarkInk,
  MAP_LABEL_FONTS,
  mapPaperColor,
  PATH_WEIGHT_PX,
  type MapDocument,
  type MapFill,
  type MapImage,
  type MapLabel,
  type MapLandmark,
  type MapLayer,
  type MapPath,
} from "./mapDocument";
import { mapPage } from "./mapPage";
import { isJust, isNothing, just, nothing, toMaybe, type Maybe } from "./maybe";
import { isLandmassVisible } from "./mapLandmass";

export type MapToSvgBounds = "page" | "content";

export type MapToSvgOptions = {
  /** Project-relative asset path → data URL (images + landmass mask). */
  readonly assetDataUrls?: ReadonlyMap<string, string> | Record<string, string>;
  /** Extra padding around content bounds (ignored when bounds is "page"). */
  readonly padding?: number;
  /**
   * `page` matches editor PNG export (page frame). `content` uses the ink
   * bounding box plus padding.
   */
  readonly bounds?: MapToSvgBounds;
};

function assetUrl(
  assets: MapToSvgOptions["assetDataUrls"],
  path: string,
): Maybe<string> {
  if (typeof assets === "undefined") return nothing;
  if (assets instanceof Map) return toMaybe(assets.get(path));
  const record = assets as Record<string, string>;
  return toMaybe(record[path]);
}

/** Escape text for XML attribute and text content. */
export function escapeXml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function dashArray(style: MapPath["style"], width: number): Maybe<string> {
  const dash = pathLineDash(style ?? "solid", width);
  if (dash.length === 0) return nothing;
  return just(dash.join(" "));
}

/** Catmull-Rom → cubic Bézier SVG path `d`, matching canvas `traceSmoothPath`. */
export function smoothPathD(
  points: ReadonlyArray<WorldPoint>,
  toScreen: (p: WorldPoint) => ScreenPoint,
  closed = false,
): string {
  if (points.length < 2) return "";
  const screen = points.map(toScreen);
  const n = screen.length;
  const parts: string[] = [`M ${screen[0]!.x} ${screen[0]!.y}`];
  if (n === 2) {
    parts.push(`L ${screen[1]!.x} ${screen[1]!.y}`);
    if (closed) parts.push("Z");
    return parts.join(" ");
  }
  const at = (i: number): ScreenPoint => screen[((i % n) + n) % n]!;
  const last = closed ? n : n - 1;
  for (let i = 0; i < last; i++) {
    const p0 = closed ? at(i - 1) : screen[Math.max(i - 1, 0)]!;
    const p1 = screen[i]!;
    const p2 = closed ? at(i + 1) : screen[i + 1]!;
    const p3 = closed ? at(i + 2) : screen[Math.min(i + 2, n - 1)]!;
    const { cp1, cp2 } = catmullRomCubicControls(p0, p1, p2, p3);
    parts.push(`C ${cp1.x} ${cp1.y} ${cp2.x} ${cp2.y} ${p2.x} ${p2.y}`);
  }
  if (closed) parts.push("Z");
  return parts.join(" ");
}

function estimateGlyphWidth(text: string, fontPx: number): number {
  let width = 0;
  const segmenter = new Intl.Segmenter([], { granularity: "grapheme" });
  for (const { segment } of segmenter.segment(text)) {
    width += segment === " " ? fontPx * 0.33 : fontPx * 0.55;
  }
  return width;
}

function labelGlyphs(
  label: MapLabel,
  zoom: number,
): {
  glyphs: ReturnType<typeof layoutMapLabel>["glyphs"];
  fontPx: number;
  fontFamily: string;
  fill: string;
} {
  const fontPx = labelFontPx(label, zoom);
  const fontFamily = MAP_LABEL_FONTS[label.font ?? "cartographic"];
  const curve = label.curve ?? 0;
  const spacing = (label.letterSpacing ?? 0) * zoom;
  const lines = label.text.split("\n").map((line) => {
    if (curve === 0 && spacing === 0) {
      return [{ text: line, width: estimateGlyphWidth(line, fontPx) }];
    }
    const segmenter = new Intl.Segmenter([], { granularity: "grapheme" });
    return Array.from(segmenter.segment(line), ({ segment }) => ({
      text: segment,
      width: estimateGlyphWidth(segment, fontPx),
    }));
  });
  const layout = layoutMapLabel(
    lines,
    fontPx,
    curve,
    spacing,
    label.rotation ?? 0,
  );
  return {
    glyphs: layout.glyphs,
    fontPx,
    fontFamily,
    fill: labelInk(label),
  };
}

function tintLandmarkSvg(svg: string, color: string): string {
  return svg
    .replace(/\scurrentColor\b/g, ` ${color}`)
    .replace(/="currentColor"/g, `="${color}"`)
    .replace(/='currentColor'/g, `='${color}'`);
}

function landmarkInner(
  landmark: MapLandmark,
  size: { width: number; height: number },
): string {
  const ink = landmarkInk(landmark);
  if (typeof landmark.iconId === "string" && landmark.iconId.length > 0) {
    const icon = landmarkIconById(landmark.iconId);
    if (isJust(icon)) {
      if (typeof icon.value.svg === "string" && icon.value.svg.length > 0) {
        const tinted = tintLandmarkSvg(icon.value.svg, ink);
        const encoded = encodeURIComponent(tinted);
        return `<image href="data:image/svg+xml;charset=utf-8,${encoded}" x="${-size.width / 2}" y="${-size.height / 2}" width="${size.width}" height="${size.height}" />`;
      }
      const paths = icon.value.paths;
      if (typeof paths !== "undefined" && paths.length > 0) {
        const vb = icon.value.viewBox ?? "0 0 24 24";
        const parts = vb.trim().split(/\s+/).map(Number);
        const minX = parts[0] ?? 0;
        const minY = parts[1] ?? 0;
        const vbW = parts[2] ?? 24;
        const vbH = parts[3] ?? 24;
        const sx = size.width / vbW;
        const sy = size.height / vbH;
        const ds = paths
          .map(
            (d) =>
              `<path d="${escapeXml(d)}" fill="none" stroke="${escapeXml(ink)}" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" />`,
          )
          .join("");
        return `<g transform="translate(${-size.width / 2} ${-size.height / 2}) scale(${sx} ${sy}) translate(${-minX} ${-minY})">${ds}</g>`;
      }
    }
  }
  if (typeof landmark.emoji === "string" && landmark.emoji.length > 0) {
    return `<text text-anchor="middle" dominant-baseline="central" font-size="${size.width}" font-family='"Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif">${escapeXml(landmark.emoji)}</text>`;
  }
  return "";
}

function contentBounds(doc: MapDocument): {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
} {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const include = (x: number, y: number): void => {
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  };
  for (const layer of doc.layers) {
    for (const node of layer.nodes) include(node.x, node.y);
    for (const lm of layer.landmarks) {
      const size = landmarkDrawSize(lm, 1);
      include(lm.x - size.width / 2, lm.y - size.height / 2);
      include(lm.x + size.width / 2, lm.y + size.height / 2);
    }
    for (const image of layer.images) {
      const size = mapImageDrawSize(image, 1);
      include(image.x - size.width / 2, image.y - size.height / 2);
      include(image.x + size.width / 2, image.y + size.height / 2);
    }
  }
  for (const label of doc.labels) include(label.x, label.y);
  const lm = doc.landmass;
  if (typeof lm !== "undefined" && isLandmassVisible(lm)) {
    include(lm.world.x, lm.world.y);
    include(lm.world.x + lm.world.w, lm.world.y + lm.world.h);
  }
  if (
    Number.isFinite(minX) === false ||
    Number.isFinite(minY) === false ||
    Number.isFinite(maxX) === false ||
    Number.isFinite(maxY) === false
  ) {
    const page = mapPage(doc);
    const cx = page.centerX ?? 0;
    const cy = page.centerY ?? 0;
    return {
      minX: cx - page.width / 2,
      minY: cy - page.height / 2,
      maxX: cx + page.width / 2,
      maxY: cy + page.height / 2,
    };
  }
  return { minX, minY, maxX, maxY };
}

function resolveView(
  doc: MapDocument,
  options: MapToSvgOptions,
): {
  width: number;
  height: number;
  centerX: number;
  centerY: number;
} {
  const mode = options.bounds ?? "page";
  if (mode === "page") {
    const page = mapPage(doc);
    return {
      width: page.width,
      height: page.height,
      centerX: page.centerX ?? 0,
      centerY: page.centerY ?? 0,
    };
  }
  const pad = Math.max(0, options.padding ?? 24);
  const box = contentBounds(doc);
  return {
    width: Math.max(64, Math.ceil(box.maxX - box.minX + pad * 2)),
    height: Math.max(64, Math.ceil(box.maxY - box.minY + pad * 2)),
    centerX: (box.minX + box.maxX) / 2,
    centerY: (box.minY + box.maxY) / 2,
  };
}

function renderImage(
  image: MapImage,
  toScreen: (p: WorldPoint) => ScreenPoint,
  assets: MapToSvgOptions["assetDataUrls"],
): string {
  const hrefM = assetUrl(assets, image.path);
  if (isNothing(hrefM) || hrefM.value.length === 0) return "";
  const href = hrefM.value;
  if (href.startsWith("data:") === false && href.startsWith("#") === false) {
    // Refuse external / relative links; only embedded data URLs.
    return "";
  }
  const size = mapImageDrawSize(image, 1);
  const p = toScreen(image);
  const rot = image.rotation ?? 0;
  const sx = image.flipX === true ? -1 : 1;
  const sy = image.flipY === true ? -1 : 1;
  return `<g transform="translate(${p.x} ${p.y}) rotate(${rot}) scale(${sx} ${sy})"><image href="${escapeXml(href)}" x="${-size.width / 2}" y="${-size.height / 2}" width="${size.width}" height="${size.height}" preserveAspectRatio="none" /></g>`;
}

function renderLandmark(
  landmark: MapLandmark,
  toScreen: (p: WorldPoint) => ScreenPoint,
): string {
  const size = landmarkDrawSize(landmark, 1);
  const p = toScreen(landmark);
  const rot = landmark.rotation ?? 0;
  const sx = landmark.flipX === true ? -1 : 1;
  const sy = landmark.flipY === true ? -1 : 1;
  const inner = landmarkInner(landmark, size);
  if (inner.length === 0) return "";
  return `<g transform="translate(${p.x} ${p.y}) rotate(${rot}) scale(${sx} ${sy})">${inner}</g>`;
}

function renderLabel(
  label: MapLabel,
  toScreen: (p: WorldPoint) => ScreenPoint,
): string {
  const p = toScreen(label);
  const { glyphs, fontPx, fontFamily, fill } = labelGlyphs(label, 1);
  const texts = glyphs
    .map((glyph) => {
      const deg = (glyph.angle * 180) / Math.PI;
      return `<text x="0" y="0" transform="translate(${glyph.x} ${glyph.y}) rotate(${deg})" text-anchor="middle" dominant-baseline="middle" font-size="${fontPx}" font-family="${escapeXml(fontFamily)}" fill="${escapeXml(fill)}">${escapeXml(glyph.text)}</text>`;
    })
    .join("");
  return `<g transform="translate(${p.x} ${p.y})">${texts}</g>`;
}

function renderPath(
  layer: MapLayer,
  path: MapPath,
  toScreen: (p: WorldPoint) => ScreenPoint,
): string {
  const points = pathPoints(layer, path);
  if (points.length < 2) return "";
  const width = PATH_WEIGHT_PX[path.weight];
  const d = smoothPathD(points, toScreen, false);
  const dashM = dashArray(path.style, width);
  const dashAttr = isNothing(dashM) ? "" : ` stroke-dasharray="${dashM.value}"`;
  return `<path d="${d}" fill="none" stroke="${escapeXml(path.color)}" stroke-width="${width}" stroke-linecap="round" stroke-linejoin="round"${dashAttr} />`;
}

function renderFill(
  layer: MapLayer,
  fill: MapFill,
  toScreen: (p: WorldPoint) => ScreenPoint,
): string {
  const points = fillPoints(layer, fill);
  if (points.length < 3) return "";
  const d = smoothPathD(points, toScreen, true);
  return `<path d="${d}" fill="${escapeXml(fill.color)}" fill-opacity="${MAP_FILL_ALPHA}" stroke="none" />`;
}

function renderLayer(
  layer: MapLayer,
  toScreen: (p: WorldPoint) => ScreenPoint,
  assets: MapToSvgOptions["assetDataUrls"],
): string {
  if (layer.visible === false) return "";
  const parts: string[] = [];
  for (const fill of fillsBackToFront(layer)) {
    parts.push(renderFill(layer, fill, toScreen));
  }
  for (const path of layer.paths) {
    parts.push(renderPath(layer, path, toScreen));
  }
  for (const image of layer.images) {
    parts.push(renderImage(image, toScreen, assets));
  }
  for (const landmark of layer.landmarks) {
    parts.push(renderLandmark(landmark, toScreen));
  }
  const opacity = layer.opacity ?? 1;
  const body = parts.filter((p) => p.length > 0).join("");
  if (body.length === 0) return "";
  if (opacity >= 1) return body;
  if (opacity <= 0) return "";
  return `<g opacity="${opacity}">${body}</g>`;
}

function renderLandmass(
  doc: MapDocument,
  toScreen: (p: WorldPoint) => ScreenPoint,
  assets: MapToSvgOptions["assetDataUrls"],
): string {
  const landmass = doc.landmass;
  if (
    typeof landmass === "undefined" ||
    isLandmassVisible(landmass) === false
  ) {
    return "";
  }
  const hrefM = assetUrl(assets, landmass.path);
  if (isNothing(hrefM) || hrefM.value.startsWith("data:") === false) {
    return "";
  }
  const href = hrefM.value;
  const topLeft = toScreen({ x: landmass.world.x, y: landmass.world.y });
  const bottomRight = toScreen({
    x: landmass.world.x + landmass.world.w,
    y: landmass.world.y + landmass.world.h,
  });
  const w = bottomRight.x - topLeft.x;
  const h = bottomRight.y - topLeft.y;
  if (w === 0 || h === 0) return "";
  // Embed the mask image; contour-trace SVG polygons remain a follow-up.
  return `<image href="${escapeXml(href)}" x="${topLeft.x}" y="${topLeft.y}" width="${w}" height="${h}" opacity="0.9" preserveAspectRatio="none" />`;
}

function renderFrame(
  doc: MapDocument,
  toScreen: (p: WorldPoint) => ScreenPoint,
): string {
  const page = mapPage(doc);
  if (page.frame === "none") return "";
  const cx = page.centerX ?? 0;
  const cy = page.centerY ?? 0;
  const inset = Math.min(page.width, page.height) * 0.025;
  const x = page.width / 2 - inset;
  const y = page.height / 2 - inset;
  const corners = [
    { x: cx - x, y: cy - y },
    { x: cx + x, y: cy - y },
    { x: cx + x, y: cy + y },
    { x: cx - x, y: cy + y },
  ];
  const d = smoothPathD(corners, toScreen, true);
  const parts = [
    `<path d="${d}" fill="none" stroke="#51483c" stroke-opacity="0.65" stroke-width="1.3" stroke-linejoin="round" />`,
  ];
  if (page.frame !== "pencil") {
    const gap = Math.max(4, inset * 0.22);
    const x2 = page.width / 2 - inset - gap;
    const y2 = page.height / 2 - inset - gap;
    const inner = [
      { x: cx - x2, y: cy - y2 },
      { x: cx + x2, y: cy - y2 },
      { x: cx + x2, y: cy + y2 },
      { x: cx - x2, y: cy + y2 },
    ];
    parts.push(
      `<path d="${smoothPathD(inner, toScreen, true)}" fill="none" stroke="#51483c" stroke-opacity="0.45" stroke-width="0.8" stroke-linejoin="round" />`,
    );
  }
  return parts.join("");
}

/**
 * Render a map document to a self-contained SVG string.
 * Callers supply asset data URLs for placeable images and landmass masks.
 */
export function mapToSvg(
  doc: MapDocument,
  options: MapToSvgOptions = {},
): string {
  const view = resolveView(doc, options);
  const width = view.width;
  const height = view.height;
  const viewport = { x: view.centerX, y: view.centerY, zoom: 1 };
  const toScreen = (p: WorldPoint): ScreenPoint =>
    worldToScreen(p, viewport, width, height);
  const assets = options.assetDataUrls;

  const parts: string[] = [];
  if (isPaperVisible(doc)) {
    parts.push(
      `<rect width="100%" height="100%" fill="${escapeXml(mapPaperColor(doc))}" />`,
    );
  } else {
    parts.push(`<rect width="100%" height="100%" fill="#ffffff" />`);
  }
  parts.push(renderLandmass(doc, toScreen, assets));
  for (const layer of doc.layers) {
    parts.push(renderLayer(layer, toScreen, assets));
  }
  if (isLabelsVisible(doc)) {
    for (const label of doc.labels) {
      parts.push(renderLabel(label, toScreen));
    }
  }
  parts.push(renderFrame(doc, toScreen));

  const body = parts.filter((p) => p.length > 0).join("");
  return `<?xml version="1.0" encoding="UTF-8"?>\n<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">${body}</svg>`;
}

/** Data-URL form for `<img src>` / manuscript preview. */
export function mapToSvgDataUrl(
  doc: MapDocument,
  options: MapToSvgOptions = {},
): string {
  const svg = mapToSvg(doc, options);
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

export function mapSvgPixelSize(
  doc: MapDocument,
  options: MapToSvgOptions = {},
): { width: number; height: number } {
  const view = resolveView(doc, options);
  return { width: view.width, height: view.height };
}
